// src/pages/CustomerTransactions.jsx
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import { FaFilter, FaRedoAlt, FaReceipt } from "react-icons/fa";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";
import { useGetCustomerTransactionsQuery } from "../toolkitServices/customerTransactionApi";
import { handleDropDowns } from "../config/dropDownHelpers";

/* ------------ Tiny spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ------------ Helpers ------------ */
const formatDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const up = (x) => String(x || "").toUpperCase();

/* ------------ UI atoms ------------ */
const MoneyPill = ({ value, tone = "default" }) => {
  const num = Number(value) || 0;

  const toneCls =
    tone === "credit"
      ? "bg-emerald-50 ring-emerald-200 text-emerald-800"
      : tone === "debit"
      ? "bg-rose-50 ring-rose-200 text-rose-800"
      : "bg-slate-50 ring-slate-200 text-slate-800";

  return (
    <span
      className={`inline-flex items-center justify-end gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums tracking-tight ring-1 shadow-sm ${toneCls}`}
      title={`₹ ${formatAmount(num)}`}
    >
      <span className="text-[10px] opacity-70">₹</span>
      {formatAmount(num)}
    </span>
  );
};

const badgeClassesByType = {
  OPENING: "bg-blue-50 text-blue-700 border border-blue-100",
  INVOICE: "bg-emerald-50 text-emerald-700 border-emerald-100",
  URD: "bg-orange-50 text-orange-700 border-orange-100",
  PAYMENT: "bg-indigo-50 text-indigo-700 border-indigo-100",
  WALLET: "bg-pink-50 text-pink-700 border-pink-100",
  INVOICE_RETURN: "bg-amber-50 text-amber-700 border-amber-100",
  SALES_RETURN: "bg-amber-50 text-amber-700 border-amber-100",
  REFUND: "bg-rose-50 text-rose-700 border-rose-100",
  SCHEME: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100",
};

const typeLabel = (t, row) => {
  const T = up(t);
  if (!T) return "";
  if (T === "WALLET") {
    const dir = up(row?.walletDirection);
    if (dir === "CREDIT") return "Wallet Top-up";
    if (dir === "DEBIT") return "Wallet Withdraw";
    return "Wallet";
  }
  switch (T) {
    case "INVOICE":
      return "Invoice";
    case "URD":
      return "URD";
    case "PAYMENT":
      return "Receipt";
    case "OPENING":
      return "Opening";
    case "SCHEME":
      return "Scheme";
    case "SALES_RETURN":
    case "INVOICE_RETURN":
      return "Sales Return";
    case "REFUND":
      return "Refund";
    default:
      return T;
  }
};

const getLedgerAmountValue = (r) => {
  const tokens = String(r?.note || "").split(" ");
  const lastToken = tokens?.[tokens.length - 1] || "";
  const fallbackFromNote = String(lastToken).replace(/[^\d.]/g, "");
  const fallbackNum = fallbackFromNote ? Number(fallbackFromNote) : 0;

  return (
    Number(r?.credit) ||
    Number(r?.invoicePaid) ||
    Number(r?.debit) ||
    (Number.isFinite(fallbackNum) ? fallbackNum : 0)
  );
};

const getLedgerType = (r) => up(r?.sourceType || r?.type || r?.kind);

const isReturnLikeTxn = (r) => {
  const type = getLedgerType(r);
  const walletDirection = up(r?.walletDirection);

  return (
    type === "SALES_RETURN" ||
    type === "INVOICE_RETURN" ||
    type === "REFUND" ||
    (type === "WALLET" && walletDirection === "DEBIT")
  );
};

const isReceivableTxn = (r) => {
  const type = getLedgerType(r);
  return type === "INVOICE" || (type === "OPENING" && Number(r?.debit) > 0);
};

const isReceivedTxn = (r) => {
  if (isReceivableTxn(r) || isReturnLikeTxn(r)) return false;
  return getLedgerAmountTone(r) === "credit";
};

const getLedgerAmountTone = (r) => {
  const type = getLedgerType(r);
  const walletDirection = up(r?.walletDirection);

  // These transactions represent value moving from the business back to the
  // customer, regardless of which amount field the API uses internally.
  if (isReturnLikeTxn(r)) {
    return "debit";
  }

  if (type === "WALLET" && walletDirection === "CREDIT") return "credit";
  if (Number(r?.credit || r?.invoicePaid) > 0) return "credit";
  if (Number(r?.debit) > 0) return "debit";
  return "default";
};

/* ====================================================================== */
/*                              MAIN SCREEN                               */
/* ====================================================================== */
const CustomerTransactions = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [customer, setCustomer] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: customerRes, isLoading: customersLoading } =
    useGetAllCustomersQuery({
      search: "",
      page: 1,
      limit: 500,
    });

  const rawCustomers = useMemo(
    () => customerRes?.rows || customerRes?.data || [],
    [customerRes]
  );

  const customerOptions = useMemo(
    () =>
      handleDropDowns({
        key: "customer",
        customers: rawCustomers,
      }) || [],
    [rawCustomers]
  );

  const transArgs = useMemo(() => {
    const base = { page, limit, from, to, q };
    if (customer?.value) base.customerId = customer.value;
    return base;
  }, [page, limit, from, to, q, customer]);

  const {
    data: transRes,
    isLoading,
    isFetching,
    refetch,
  } = useGetCustomerTransactionsQuery(transArgs);

  const rawRows = useMemo(() => transRes?.rows || [], [transRes]);
  const rows = rawRows;
  const total = Number(transRes?.total || 0) || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const pageSummary = useMemo(
    () => {
      const summary = rows.reduce(
        (acc, row) => {
          const value = getLedgerAmountValue(row);

          if (isReceivableTxn(row)) acc.totalReceivable += value;
          if (isReceivedTxn(row)) acc.received += value;
          if (isReturnLikeTxn(row)) acc.returned += value;

          return acc;
        },
        {
          totalReceivable: 0,
          received: 0,
          returned: 0,
          netReceived: 0,
          outstanding: 0,
        },
      );

      summary.netReceived = summary.received - summary.returned;
      summary.outstanding = Math.max(
        summary.totalReceivable - summary.netReceived,
        0,
      );
      return summary;
    },
    [rows],
  );

  const activeFiltersText = useMemo(() => {
    const parts = [];
    if (from) parts.push(`From: ${from}`);
    if (to) parts.push(`To: ${to}`);
    if (q) parts.push(`Search: ${q}`);
    if (customer?.label) parts.push(`Customer: ${customer.label}`);
    return parts.length ? parts.join(" · ") : "Showing all transactions";
  }, [from, to, q, customer]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      height: 34,
      borderRadius: 999,
      borderColor: state.isFocused ? "#6366f1" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #6366f133" : "none",
      "&:hover": { borderColor: "#6366f1" },
      fontSize: "0.8rem",
      backgroundColor: "white",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    singleValue: (base) => ({
      ...base,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "#111827",
    }),
    placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
    indicatorsContainer: (base) => ({ ...base, height: 34 }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.8rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      backgroundColor: state.isFocused
        ? "#e0e7ff"
        : state.isSelected
        ? "#c7d2fe"
        : "white",
      color: "#111827",
    }),
    menu: (base) => ({ ...base, zIndex: 40 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const paginationSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 8,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused
        ? "0 0 0 1px rgba(148,129,206,.25)"
        : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_18px_50px_-24px_rgba(71,55,128,0.45)] md:h-[calc(100vh-70px)]">
      {/* Header bar */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaReceipt className="text-sm" />
          </span>
          <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] sm:text-lg font-semibold tracking-tight">
              Customer Transactions
            </h2>
            {isFetching && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
                <Spinner size={10} /> refreshing…
              </span>
            )}
          </div>
          <p className="mt-0.5 hidden truncate text-[11px] text-violet-50 sm:block">
            A complete ledger of customer money movement
          </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((p) => !p)}
            className="hidden h-9 items-center gap-2 rounded-xl bg-white/15 px-3 text-[11px] font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:inline-flex"
            title="Filters"
          >
            <FaFilter className="text-xs" />
            Filters
            {filtersOpen ? <FiChevronUp /> : <FiChevronDown />}
          </button>

          <button
            onClick={() => refetch?.()}
            disabled={isFetching}
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 disabled:opacity-60 sm:rounded-full"
          >
            <FaRedoAlt className="text-xs" />
            Refresh
          </button>
        </div>
      </div>

      {/* Top bar */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <Select
              isClearable
              isLoading={customersLoading}
              options={customerOptions}
              value={customer}
              onChange={(val) => {
                setCustomer(val || null);
                setPage(1);
              }}
              placeholder="Select customer (optional)..."
              classNamePrefix="react-select"
              styles={selectStyles}
              menuPlacement="auto"
              menuPortalTarget={document.body}
            />
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen((p) => !p)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-violet-100 bg-white text-[#6b59c6] shadow-sm transition hover:bg-violet-50 sm:hidden"
            title="Filters"
          >
            {filtersOpen ? (
              <FiChevronUp className="text-[16px]" />
            ) : (
              <FaFilter className="text-[14px]" />
            )}
          </button>
        </div>

        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-[10px] text-slate-500 truncate">
            {activeFiltersText}
          </span>

          {(from || to || q || customer) && (
            <button
              type="button"
              onClick={() => {
                setFrom("");
                setTo("");
                setQ("");
                setCustomer(null);
                setPage(1);
              }}
              className="text-[10px] px-2 py-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 shrink-0"
            >
              Clear
            </button>
          )}
        </div>

        <div className="mt-2 flex snap-x snap-mandatory gap-1.5 overflow-x-auto overscroll-x-contain scroll-smooth pb-0.5 text-[10px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:snap-none sm:grid-cols-5 sm:overflow-visible sm:pb-0">
          <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-violet-100 bg-violet-50/70 px-2 text-violet-700 sm:min-w-0">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
            <span className="shrink-0 font-medium">Receivable</span>
            <span className="truncate font-bold tabular-nums">
              ₹ {formatAmount(pageSummary.totalReceivable)}
            </span>
          </div>
          <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50/70 px-2 text-emerald-700 sm:min-w-0">
            <FaArrowTrendUp className="shrink-0" />
            <span className="shrink-0 font-medium">Received</span>
            <span className="truncate font-bold tabular-nums">
              ₹ {formatAmount(pageSummary.received)}
            </span>
          </div>
          <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-rose-100 bg-rose-50/70 px-2 text-rose-700 sm:min-w-0">
            <FaArrowTrendDown className="shrink-0" />
            <span className="shrink-0 font-medium">Returned</span>
            <span className="truncate font-bold tabular-nums">
              ₹ {formatAmount(pageSummary.returned)}
            </span>
          </div>
          <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 text-slate-700 sm:min-w-0">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
            <span className="shrink-0 font-medium">Net</span>
            <span className="truncate font-bold tabular-nums">
              ₹ {formatAmount(pageSummary.netReceived)}
            </span>
          </div>
          <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50/70 px-2 text-amber-700 sm:min-w-0">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
            <span className="shrink-0 font-medium">Due</span>
            <span className="truncate font-bold tabular-nums">
              ₹ {formatAmount(pageSummary.outstanding)}
            </span>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {filtersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="mt-2 overflow-hidden"
            >
              <div className="rounded-2xl border border-violet-100 bg-white p-3 shadow-[0_12px_30px_-20px_rgba(71,55,128,0.5)]">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="col-span-1">
                    <label className="text-[11px] font-medium text-slate-700">
                      From
                    </label>
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => {
                        setFrom(e.target.value);
                        setPage(1);
                      }}
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="text-[11px] font-medium text-slate-700">
                      To
                    </label>
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => {
                        setTo(e.target.value);
                        setPage(1);
                      }}
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-2">
                    <label className="text-[11px] font-medium text-slate-700">
                      Search
                    </label>
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => {
                        setQ(e.target.value);
                        setPage(1);
                      }}
                      placeholder="Invoice no, note, reference..."
                      className="mt-1 w-full px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>

                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500">
                    Apply filters and close panel.
                  </span>

                  <button
                    type="button"
                    onClick={() => setFiltersOpen(false)}
                    className="px-3 py-1 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-50"
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Table + Sticky Pagination wrapper */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Scroll only this area */}
        <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
          {isLoading && !rows.length && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-20">
              <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
                <Spinner size={22} />
                <span>Loading transactions…</span>
              </div>
            </div>
          )}

          <table className="w-full min-w-[900px] text-xs md:text-sm">
            <thead className="sticky top-0 z-10 bg-[#efeafd] text-[#4b3d91] shadow-sm">
              <tr className="text-[10px] font-semibold uppercase tracking-wide md:text-[11px]">
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Ref</th>
                <th className="px-3 py-2 text-left">Details</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-gray-400 text-sm"
                  >
                    Loading transactions...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-gray-400 text-sm"
                  >
                    No transactions found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const t = up(r.sourceType || r.type);
                  const amountValue = getLedgerAmountValue(r);
                  const tone = getLedgerAmountTone(r);

                  return (
                    <tr
                      key={r._id || `${r.sourceType}-${r.sourceId}-${r.date}`}
                      className="border-t border-slate-100 transition hover:bg-[#f7f5ff]"
                    >
                      <td className="px-3 py-2 align-top text-gray-700">
                        <div className="flex flex-col">
                          <span className="text-xs font-medium">
                            {formatDateTime(r.date || r.invDate || r.createdAt)}
                          </span>
                          {r.branchName && (
                            <span className="text-[10px] text-gray-400">
                              {r.branchName}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-3 py-2 align-top text-gray-700">
                        {!r?.customerId ? (
                          <span className="text-xs text-slate-700">—</span>
                        ) : (
                          <div className="flex flex-col">
                            <span className="text-xs font-medium text-slate-800">
                              {r?.customerId || "-"}
                            </span>
                           
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2 align-top">
                        <span
                          className={`inline-flex items-center justify-center min-w-[104px] px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                            badgeClassesByType[t] ||
                            "bg-gray-50 text-gray-600 border border-gray-200"
                          }`}
                        >
                          {typeLabel(t, r)}
                        </span>
                      </td>

                      <td className="px-3 py-2 align-top text-gray-700">
                        <div className="flex flex-col">
                          <span className="text-xs font-medium">
                            {r.sourceNo || r.invoiceRef || r.sourceRef || "-"}
                          </span>
                          {(r.paymentMode || r.paymentRefNo) && (
                            <span className="text-[10px] text-gray-400">
                              {r.paymentMode || ""}
                              {r.paymentRefNo ? ` · ${r.paymentRefNo}` : ""}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-3 py-2 align-top text-gray-600">
                        {r.note ? (
                          <span className="text-xs line-clamp-2">{r.note}</span>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="px-3 py-2 align-top text-right">
                        <MoneyPill value={amountValue} tone={tone} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Sticky pagination */}
        <div className="z-30 flex shrink-0 items-center justify-between gap-2 border-t bg-slate-50/80 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
          <div className="hidden text-slate-500 sm:block">
            Page <span className="font-semibold">{page}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>{" "}
            <span className="text-slate-400">· Total: {total || rows.length}</span>
          </div>

          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
            <div className="flex shrink-0 items-center gap-1 text-slate-600">
              <span className="hidden text-xs sm:inline">Rows / page</span>
              <Select
                value={{ value: limit, label: String(limit) }}
                options={[10, 25, 50, 100].map((number) => ({ value: number, label: String(number) }))}
                onChange={(option) => {
                  setLimit(Number(option?.value) || 25);
                  setPage(1);
                }}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[54px]"
                aria-label="Rows per page"
              />
              <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base shadow-sm disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              type="button"
            >
              <span className="sm:hidden">&lsaquo;</span><span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{ value: page, label: String(page) }}
                options={Array.from({ length: totalPages }, (_, index) => ({ value: index + 1, label: String(index + 1) }))}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page"
              />
              <span>/ {totalPages}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base shadow-sm disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              type="button"
            >
              <span className="sm:hidden">&rsaquo;</span><span className="hidden sm:inline">Next</span>
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerTransactions;
